import { useEffect, useMemo, useState } from 'react'
import { Link, useLocation, useParams } from 'react-router-dom'
import Card from '@/components/ui/Card'
import { useAuth } from '@/lib/auth-context'

interface OrderItem {
  id: string
  title?: string
  sku?: string
  qty?: number
  price_cents?: number
}

interface Shipment {
  id: string
  carrier?: string
  tracking_number?: string
  status?: string
  updated_at?: string
  last_event_at?: string
  created_at?: string
  eta?: string
}

interface Order {
  order_number?: string
  subtotal_cents?: number
  total_cents?: number
  status?: string
  order_items?: OrderItem[]
  shipments?: Shipment[]
}

interface SessionData {
  status?: string
  created_at?: string
  email_alias?: string
  partner?: string
  budget_cents?: number
  amount_cents?: number
  merchant_name?: string
  orders?: Order[]
}

const SESSION_STATUS_THEME: Record<string, string> = {
  started: 'bg-sand text-cocoa',
  authorized: 'bg-amber-200 text-amber-900',
  captured: 'bg-emerald-100 text-emerald-900',
  failed: 'bg-brick/10 text-brick',
  canceled: 'bg-cocoa/10 text-cocoa/80',
}

const ORDER_STATUS_THEME: Record<string, string> = {
  confirmed: 'bg-sky-100 text-sky-900',
  shipped: 'bg-indigo-100 text-indigo-900',
  delivered: 'bg-emerald-100 text-emerald-900',
  canceled: 'bg-brick/10 text-brick',
}

function classNames(...values: (string | boolean | null | undefined)[]): string {
  return values.filter(Boolean).join(' ')
}

function StatusBadge({ status, kind = 'session' }: { status?: string; kind?: 'session' | 'order' }) {
  if (!status) return null
  const theme = kind === 'order' ? ORDER_STATUS_THEME : SESSION_STATUS_THEME
  return (
    <span className={classNames('inline-flex items-center rounded-full px-2.5 py-1 text-xs font-semibold', theme[status] || 'bg-shell text-cocoa')}>
      {formatStatus(status)}
    </span>
  )
}

function formatStatus(status: string = ''): string {
  return status.replace(/_/g, ' ').replace(/\b\w/g, (m) => m.toUpperCase())
}

function formatCurrency(cents: number | null | undefined): string {
  if (cents === null || cents === undefined || !Number.isFinite(cents)) return '—'
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(cents / 100)
}

function formatDate(value: string | null | undefined): string {
  if (!value) return '—'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return '—'
  return date.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  })
}

export default function TeacherOrderStatus() {
  const { id } = useParams()
  const location = useLocation()
  const { session } = useAuth()
  const [data, setData] = useState<SessionData | null>(null)
  const [loading, setLoading] = useState<boolean>(true)
  const [error, setError] = useState<string>('')

  const basePath = '/portal/teacher'

  useEffect(() => {
    if (!id) return
    let cancelled = false

    const load = async () => {
      setLoading(true)
      setError('')
      try {
        const CORE_API = import.meta.env.VITE_CORE_API_URL || ''
        const response = await fetch(`${CORE_API}/api/v1/shopping/session?id=${encodeURIComponent(id)}`, {
          headers: {
            Authorization: `Bearer ${session?.access_token || ''}`,
          },
        })

        const raw = await response.json().catch(() => ({}))
        if (!response.ok) {
          const errMsg = typeof raw?.error === 'string' ? raw.error : raw?.error?.message
          throw new Error(errMsg || 'Unable to load session')
        }

        const body = raw?.data ?? raw
        if (!cancelled) {
          setData(body.session || null)
        }
      } catch (err: unknown) {
        if (!cancelled) {
          setError((err as Error).message)
          setData(null)
        }
      } finally {
        if (!cancelled) setLoading(false)
      }
    }

    load()

    return () => {
      cancelled = true
    }
  }, [id, session?.access_token])

  const order = data?.orders?.[0] || null
  const shipments = useMemo((): Shipment[] => {
    if (!order?.shipments) return []
    return [...order.shipments].sort((a: Shipment, b: Shipment) => {
      const aTime = new Date(a.updated_at || a.last_event_at || a.created_at || '').getTime()
      const bTime = new Date(b.updated_at || b.last_event_at || b.created_at || '').getTime()
      return bTime - aTime
    })
  }, [order?.shipments])

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
        <div className="space-y-1">
          <Link to={`${basePath}/shopping`} className="text-sm font-medium text-brick hover:underline">
            ← Back to shopping
          </Link>
          <h2 className="text-2xl font-semibold text-cocoa">Shopping session details</h2>
          <p className="text-sm text-cocoa/70">
            Created {formatDate(data?.created_at)} • Alias {data?.email_alias || '—'}
          </p>
        </div>
        <StatusBadge status={data?.status} />
      </div>

      {loading && <p className="text-sm text-cocoa/70">Loading session…</p>}
      {error && !loading && <p className="text-sm text-brick">{error}</p>}

      {!loading && !error && data && (
        <div className="space-y-6">
          <Card className="space-y-4">
            <div className="grid gap-4 md:grid-cols-3">
              <div className="rounded-xl bg-shell p-4">
                <p className="text-xs uppercase tracking-wide text-cocoa/60">Partner</p>
                <p className="text-lg font-semibold text-cocoa">
                  {data.partner ? formatStatus(data.partner) : 'Unknown'}
                </p>
              </div>
              <div className="rounded-xl bg-shell p-4">
                <p className="text-xs uppercase tracking-wide text-cocoa/60">Budget</p>
                <p className="text-lg font-semibold text-cocoa">{formatCurrency(data.budget_cents)}</p>
              </div>
              <div className="rounded-xl bg-shell p-4">
                <p className="text-xs uppercase tracking-wide text-cocoa/60">Captured</p>
                <p className="text-lg font-semibold text-cocoa">
                  {data.amount_cents ? formatCurrency(data.amount_cents) : 'Pending'}
                </p>
                {data.merchant_name && <p className="text-xs text-cocoa/60">{data.merchant_name}</p>}
              </div>
            </div>
            <div className="rounded-xl border border-sand bg-white p-4">
              <p className="text-xs uppercase tracking-wide text-cocoa/60">Purchase email alias</p>
              <p className="font-mono text-sm text-cocoa">{data.email_alias || '—'}</p>
              <p className="mt-2 text-xs text-cocoa/70">
                Keep using this email for all communication about the order so tracking stays synced here.
              </p>
            </div>
          </Card>

          <Card className="space-y-4">
            <header className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
              <div>
                <h3 className="text-lg font-semibold text-cocoa">Order details</h3>
                <p className="text-sm text-cocoa/70">
                  We ingest merchant confirmation emails sent to your purchase alias.
                </p>
              </div>
              <StatusBadge status={order?.status} kind="order" />
            </header>

            {!order ? (
              <p className="text-sm text-cocoa/70">
                We are waiting for the order confirmation email. Once it arrives, line items and tracking will appear here.
              </p>
            ) : (
              <div className="space-y-5">
                <div className="grid gap-4 md:grid-cols-3">
                  <div className="rounded-xl bg-shell p-4">
                    <p className="text-xs uppercase tracking-wide text-cocoa/60">Order number</p>
                    <p className="font-mono text-sm text-cocoa">{order.order_number || '—'}</p>
                  </div>
                  <div className="rounded-xl bg-shell p-4">
                    <p className="text-xs uppercase tracking-wide text-cocoa/60">Subtotal</p>
                    <p className="text-lg font-semibold text-cocoa">{formatCurrency(order.subtotal_cents)}</p>
                  </div>
                  <div className="rounded-xl bg-shell p-4">
                    <p className="text-xs uppercase tracking-wide text-cocoa/60">Total</p>
                    <p className="text-lg font-semibold text-cocoa">{formatCurrency(order.total_cents)}</p>
                  </div>
                </div>

                <div>
                  <h4 className="text-sm font-semibold text-cocoa">Items</h4>
                  {!order.order_items?.length ? (
                    <p className="text-sm text-cocoa/70">No line items parsed yet.</p>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-sand text-sm">
                        <thead className="bg-shell/60 text-left text-xs uppercase tracking-wide text-cocoa/70">
                          <tr>
                            <th className="px-3 py-2 font-semibold">Title</th>
                            <th className="px-3 py-2 font-semibold">SKU</th>
                            <th className="px-3 py-2 font-semibold">Qty</th>
                            <th className="px-3 py-2 font-semibold">Price</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-sand/60">
                          {order.order_items.map((item) => (
                            <tr key={item.id} className="bg-white/70">
                              <td className="px-3 py-3 text-cocoa">{item.title || '—'}</td>
                              <td className="px-3 py-3 text-cocoa/70">{item.sku || '—'}</td>
                              <td className="px-3 py-3 text-cocoa/70">{item.qty ?? '—'}</td>
                              <td className="px-3 py-3 text-cocoa/70">{formatCurrency(item.price_cents)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>

                <div>
                  <h4 className="text-sm font-semibold text-cocoa">Shipments</h4>
                  {!shipments.length ? (
                    <p className="text-sm text-cocoa/70">Tracking details will appear once the merchant ships your items.</p>
                  ) : (
                    <ul className="space-y-3">
                      {shipments.map((shipment) => (
                        <li key={shipment.id} className="rounded-xl border border-sand bg-white p-4">
                          <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
                            <div>
                              <p className="text-sm font-semibold text-cocoa">
                                {shipment.carrier || 'Carrier TBD'} — {shipment.tracking_number}
                              </p>
                              <p className="text-xs text-cocoa/70">
                                Updated {formatDate(shipment.updated_at || shipment.last_event_at || shipment.created_at)}
                              </p>
                            </div>
                            <StatusBadge status={shipment.status} />
                          </div>
                          {shipment.eta && (
                            <p className="mt-2 text-xs text-cocoa/70">Estimated arrival {formatDate(shipment.eta)}</p>
                          )}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
            )}
          </Card>
        </div>
      )}
    </div>
  )
}
